import { useState, useEffect } from "react";
import clsx from "clsx";
import { Button } from "./atoms";
import { ConfirmDeleteModal } from "./molecules/ConfirmDeleteModal";
import {
    TagSelectorPanel,
    type TagMaster,
    type TagFormValues,
} from "./TagSelectorPanel";

/**
 * シフトパターンフォームのデータ型
 */
interface ShiftPatternFormData {
    pattern_id: string;
    pattern_name: string;
    display_name: string;
    work_start_time: string;
    work_end_time: string;
    break_distribution: string;
    memo: string;
}

/**
 * API用のシフトパターンデータ型（外部からインポート可能）
 */
export interface ShiftPatternApiData {
    code: string;
    name: string;
    display_name: string | null;
    start_time: string | null;
    end_time: string | null;
    /** 休憩分数（分単位の整数。未入力/不正は 0） */
    break_time_minutes: number;
    memo: string | null;
}

export type ShiftPatternFormStep = 1 | 2;

interface TagSectionProps {
    tags: TagMaster[];
    selectedIds: number[];
    onSelectedChange: (ids: number[]) => void;
    onCreate: (data: TagFormValues) => Promise<void>;
    onUpdate: (id: number, data: TagFormValues) => Promise<void>;
    onDelete: (id: number) => Promise<void>;
    loading?: boolean;
    errorMessage?: string | null;
    /** 新規モードでの注意喚起表示 */
    showNewModeNotice?: boolean;
}

interface ShiftPatternFormProps {
    onCancel: () => void;
    onComplete: (step: ShiftPatternFormStep) => void;
    onFormDataChange?: (data: ShiftPatternApiData) => void;
    initialData?: Partial<ShiftPatternFormData>;
    isEdit?: boolean;
    onDelete?: () => void;
    isDeleting?: boolean;
    /** 2. タグタブ用のプロパティ */
    tagSection: TagSectionProps;
    /** ボタン処理中 */
    isSaving?: boolean;
    /** 現在のステップ（制御コンポーネント） */
    activeStep: ShiftPatternFormStep;
    /** ステップ切替コールバック */
    onActiveStepChange: (step: ShiftPatternFormStep) => void;
    /** 必須チェックで未入力だったフィールドキー（内部フィールド名）。空の間は赤枠表示 */
    invalidFields?: string[];
}

/**
 * サイドメニュー項目
 */
const SIDE_MENU_ITEMS: { id: ShiftPatternFormStep; label: string }[] = [
    { id: 1, label: "1. 基本情報" },
    { id: 2, label: "2. タグ" },
];

/**
 * シフトパターンフォームコンポーネント
 */
const ShiftPatternForm = ({
    onCancel,
    onComplete,
    onFormDataChange,
    initialData,
    isEdit = false,
    onDelete,
    isDeleting = false,
    tagSection,
    isSaving = false,
    activeStep,
    onActiveStepChange,
    invalidFields = [],
}: ShiftPatternFormProps) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [formData, setFormData] = useState<ShiftPatternFormData>({
        pattern_id: initialData?.pattern_id ?? "",
        pattern_name: initialData?.pattern_name ?? "",
        display_name: initialData?.display_name ?? "",
        work_start_time: initialData?.work_start_time ?? "",
        work_end_time: initialData?.work_end_time ?? "",
        break_distribution: initialData?.break_distribution ?? "",
        memo: initialData?.memo ?? "",
    });

    /**
     * 必須フィールドが未入力のままかを判定する。
     * invalidFields に含まれ、かつ現在の値が空のとき true。
     */
    const isFieldInvalid = (key: keyof ShiftPatternFormData): boolean => {
        if (!invalidFields.includes(key)) return false;
        const value = formData[key];
        return !value || !value.trim();
    };

    // フォームデータの変更を親に通知
    useEffect(() => {
        if (onFormDataChange) {
            // 「60」「60分」「60 分」等から分数を抽出（先頭の数字列のみ拾う）
            const breakDigits = formData.break_distribution.match(/\d+/)?.[0] ?? "";
            const breakMinutes = breakDigits ? parseInt(breakDigits, 10) : 0;

            const apiData: ShiftPatternApiData = {
                code: formData.pattern_id,
                name: formData.pattern_name,
                display_name: formData.display_name || null,
                start_time: formData.work_start_time || null,
                end_time: formData.work_end_time || null,
                break_time_minutes: Number.isNaN(breakMinutes) ? 0 : breakMinutes,
                memo: formData.memo || null,
            };
            onFormDataChange(apiData);
        }
    }, [formData, onFormDataChange]);

    const handleChange = (field: keyof ShiftPatternFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    // タグタブで新規モード時は保存不可
    const isTagStepSaveDisabled = activeStep === 2 && !isEdit;

    return (
        <div className="flex h-full gap-4">
            {/* サイドメニューエリア */}
            <div className="w-44 flex-shrink-0 rounded-lg bg-white py-4 flex flex-col">
                <nav className="space-y-1 flex-1">
                    {SIDE_MENU_ITEMS.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => onActiveStepChange(item.id)}
                            className={clsx(
                                "w-full px-4 py-2 text-left text-sm",
                                activeStep === item.id
                                    ? "font-medium text-brand-500"
                                    : "text-gray-600 hover:text-gray-900",
                            )}
                        >
                            {item.label}
                        </button>
                    ))}
                </nav>
                {isEdit && onDelete && (
                    <div className="px-4 pt-4 mt-auto">
                        <Button
                            variant="danger"
                            size="md"
                            onClick={() => setShowDeleteModal(true)}
                            disabled={isDeleting}
                            className="w-full"
                        >
                            削除
                        </Button>
                    </div>
                )}
            </div>

            <ConfirmDeleteModal
                isOpen={showDeleteModal}
                onCancel={() => setShowDeleteModal(false)}
                onConfirm={() => {
                    setShowDeleteModal(false);
                    onDelete?.();
                }}
                isDeleting={isDeleting}
            />

            {/* フォームコンテンツエリア（白背景・角丸） */}
            <div className="flex-1 flex bg-white rounded-lg overflow-hidden">
                <div className="flex-1 flex flex-col">
                    <div className="flex-1 p-6 overflow-y-auto">
                        {activeStep === 1 ? (
                            <BasicInfoSection
                                formData={formData}
                                onChange={handleChange}
                                isFieldInvalid={isFieldInvalid}
                            />
                        ) : (
                            <>
                                {tagSection.showNewModeNotice && !isEdit && (
                                    <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 px-4 py-2 text-sm text-yellow-800">
                                        タグの紐付けはシフトパターンを登録した後に保存されます。先に「1. 基本情報」から登録してください。
                                    </div>
                                )}
                                <TagSelectorPanel
                                    tags={tagSection.tags}
                                    selectedIds={tagSection.selectedIds}
                                    onSelectedChange={tagSection.onSelectedChange}
                                    onCreate={tagSection.onCreate}
                                    onUpdate={tagSection.onUpdate}
                                    onDelete={tagSection.onDelete}
                                    loading={tagSection.loading}
                                    errorMessage={tagSection.errorMessage}
                                />
                            </>
                        )}
                    </div>

                    {/* フッターボタン */}
                    <div className="flex justify-end gap-4 px-6 py-4">
                        {isEdit && activeStep === 2 && (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={() => onActiveStepChange(1)}
                                disabled={isSaving}
                                className="px-8"
                            >
                                1.基本情報の設定に戻る
                            </Button>
                        )}
                        <Button variant="outline" size="md" onClick={onCancel} className="px-8">
                            キャンセル
                        </Button>
                        <Button
                            variant="primary"
                            size="md"
                            onClick={() => {
                                // 編集モードの Step 1 ではまだ保存せず、タグタブへ遷移するだけ
                                if (isEdit && activeStep === 1) {
                                    onActiveStepChange(2);
                                } else {
                                    onComplete(activeStep);
                                }
                            }}
                            disabled={isSaving || isTagStepSaveDisabled}
                            className="px-8"
                        >
                            {isSaving
                                ? "保存中..."
                                : !isEdit
                                    ? "登録する"
                                    : activeStep === 1
                                        ? "2.タグの設定に進む"
                                        : "設定を完了する"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─────────── 1. 基本情報セクション ─────────── */

interface BasicInfoSectionProps {
    formData: ShiftPatternFormData;
    onChange: (field: keyof ShiftPatternFormData, value: string) => void;
    isFieldInvalid: (key: keyof ShiftPatternFormData) => boolean;
}

const BasicInfoSection = ({ formData, onChange, isFieldInvalid }: BasicInfoSectionProps) => (
    <div className="space-y-6">
        <div className="text-xs text-red-500">「*」は登録必須項目です。</div>

        <FormField label="ID" required>
            <input
                type="text"
                value={formData.pattern_id}
                onChange={(e) => onChange("pattern_id", e.target.value)}
                className={clsx(
                    "w-64 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
                    isFieldInvalid("pattern_id") ? "border-red-500" : "border-gray-300",
                )}
                placeholder="000000000000"
            />
        </FormField>

        <FormField label="名称" required>
            <input
                type="text"
                value={formData.pattern_name}
                onChange={(e) => onChange("pattern_name", e.target.value)}
                className={clsx(
                    "w-64 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
                    isFieldInvalid("pattern_name") ? "border-red-500" : "border-gray-300",
                )}
                placeholder="早番"
            />
        </FormField>

        <FormField label="略称" required>
            <input
                type="text"
                value={formData.display_name}
                onChange={(e) => onChange("display_name", e.target.value)}
                className={clsx(
                    "w-64 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
                    isFieldInvalid("display_name") ? "border-red-500" : "border-gray-300",
                )}
                placeholder="早番"
            />
        </FormField>

        <FormField label="勤務開始時刻" required>
            <input
                type="time"
                value={formData.work_start_time}
                onChange={(e) => onChange("work_start_time", e.target.value)}
                className={clsx(
                    "w-40 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
                    isFieldInvalid("work_start_time") ? "border-red-500" : "border-gray-300",
                )}
            />
        </FormField>

        <FormField label="勤務終了時刻" required>
            <input
                type="time"
                value={formData.work_end_time}
                onChange={(e) => onChange("work_end_time", e.target.value)}
                className={clsx(
                    "w-40 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
                    isFieldInvalid("work_end_time") ? "border-red-500" : "border-gray-300",
                )}
            />
        </FormField>

        <FormField label="休憩分数">
            <input
                type="text"
                value={formData.break_distribution}
                onChange={(e) => onChange("break_distribution", e.target.value)}
                className="w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="60"
            />
        </FormField>

        <FormField label="備考">
            <textarea
                value={formData.memo}
                onChange={(e) => onChange("memo", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
                rows={3}
                placeholder="備考を入力"
            />
        </FormField>
    </div>
);

interface FormFieldProps {
    label: string;
    required?: boolean;
    children: React.ReactNode;
}

const FormField = ({ label, required = false, children }: FormFieldProps) => (
    <div className="flex items-start gap-8">
        <label className="text-sm text-gray-700 w-32 flex-shrink-0 pt-2">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div className="flex-1">{children}</div>
    </div>
);

export default ShiftPatternForm;
